import { select, call, takeLatest, put } from 'redux-saga/effects';

import { PaymentActions } from '../constants';
import { PaymentApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeletePaymentAction,
    GetPaymentAction,
    GetPaymentsAction,
    PersistPaymentAction,
} from '../actions/payment-actions';
import sortingService from '../../services/sorting';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { push } from 'connected-react-router';
import { changeToZeroMomentDate } from 'src/helpers/timeHelpers';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { groupPayment } from 'src/helpers/payment';

function* handleFetchPayments(action: GetPaymentsAction) {
    const oldData = yield select((state: StoreState) => state.payments.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    let filterProps: AnyObject = { ...filters };
    if (filters?.paymentDate) {
        filterProps = {
            ...filters,
            'paymentDate[before]': changeToZeroMomentDate(`${filters.paymentDate}`),
            'paymentDate[after]': changeToZeroMomentDate(`${filters.paymentDate}`),
        };
        delete filterProps.paymentDate;
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        PaymentApi.getPayments({
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchPayment(action: GetPaymentAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => PaymentApi.getPayment(id));
}

function* handleDeletePayment(action: DeletePaymentAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => PaymentApi.deletePayment(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistPayment(action: PersistPaymentAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    const groupData = groupPayment(restData);

    yield resolveApiCall(
        action,
        () => (id ? PaymentApi.updatePayment(id, groupData) : PaymentApi.createPayment(groupData)),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);
            if (!id) {
                yield put(push(Router.generate(routes.PAYMENTS_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(PaymentActions.FETCH_PAYMENTS, handleFetchPayments);
    yield takeLatest(PaymentActions.FETCH_PAYMENT, handleFetchPayment);
    yield takeLatest(PaymentActions.DELETE_PAYMENT, handleDeletePayment);
    yield takeLatest(PaymentActions.PERSIST_PAYMENT, handlePersistPayment);
}
